import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LocalStorageService } from './local-storage.service';
import { UtilService } from './util.service';
import { LoginRequest } from '../models/login-request';
import { LoginResponse } from '../models/login-response';
import { PasswordValidResult } from '../interfaces/password-valid-result';
import { BasicResult } from '../models/basic-result';
import { ProgramFeatures } from '../models/UserManagement/program-features';
import { tap, map, Observable, of, switchMap } from 'rxjs';
import { DeviceInfo } from '../models/Utils/device-info';
import { CustomerContextService } from './customer-context.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private localStorageService: LocalStorageService,
    private httpClient: HttpClient,
    private utils: UtilService,
    private customerContext: CustomerContextService
  ) { }

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    observe: 'response' as 'response',
    responseType: 'json' as const,
    withCredentials: true,
  };

  login(username: string, password: string) {
    let req = new LoginRequest({ 'Username': username, 'Password': password });
    this.createMachineKey();
    return this.httpClient
      .post<LoginResponse>(
        `${environment.apiUrl}userauth/login`,
        req,
        this.httpOptions
      )
      .pipe(
        tap((res: any) => {
          if (!res.body?.IsError) {
            this.localStorageService.setData(1, {
              LoggedIn: true,
              Username: username.toLowerCase()
            });
          }
        }),
        switchMap((res: any) => {
          if (res.body?.IsError) {
            return of(res);
          }
          this.customerContext.reset();
          return this.customerContext.load().pipe(map(() => res));
        })
      );
  }
  createMachineKey() {
    this.localStorageService.loadData(5);
    this.localStorageService.observables.localRef
      .find((e) => {
        return e.ID == 'deviceInfo';
      })!
      .Obs.pipe(
        tap((d: any) => {
          if (d == null) this.localStorageService.setData(5, new DeviceInfo({ MachineKey: crypto.randomUUID() }));
        })
      )
      .subscribe();
 
  }
  resetPassword(email: string) {
    let req = { Email: email };
    return this.httpClient.post<BasicResult>(
      `${environment.apiUrl}userauth/resetrequest`,
      req,
      this.httpOptions
    );
  }

  resetPasswordWithToken(token: string, newPassword: string) {
    let req = { Token: token, Password: newPassword };
    return this.httpClient.post<BasicResult>(
      `${environment.apiUrl}userauth/resetwithtoken`,
      req,
      this.httpOptions
    );
  }

  isLoggedIn() {
    this.createMachineKey();
    return this.httpClient
      .get(`${environment.apiUrl}userauth/isloggedin`, this.httpOptions).pipe(
        switchMap((res: any) => {
          if (res?.status !== 200) {
            return of(res);
          }
          return this.customerContext.load().pipe(map(() => res));
        })
      );
  }

  isLoggedInOffline() {
    let loggedin: boolean = false;
    this.localStorageService.loadData(1);
    this.localStorageService.observables.localRef
      .find((e) => {
        return e.ID == 'userSettings';
      })!
      .Obs.pipe(
        tap((d: any) => {
          if (d != null) loggedin = d.LoggedIn;
        })
      )
      .subscribe();
    return loggedin;
  }

  /*signoutx() {
    this.localStorageService.clearData(1);
    return this.httpClient
      .get(`${environment.apiUrl}userauth/signout`, this.httpOptions)
      .subscribe();
  }*/
  signout() {
    return this.httpClient.get<BasicResult>(`${environment.apiUrl}userauth/signout`, this.httpOptions).pipe(
      tap(() => this.customerContext.reset())
    );
  }

  validPassword(
    newPassword: string,
    confirmPassword: string
  ): PasswordValidResult {
    let res = {
      Valid: true,
      NewPasswordErrorMessage: '',
      ConfirmPasswordErrorMessage: '',
    };

    if (newPassword.length < 8) {
      res.NewPasswordErrorMessage =
        'Password must be at least eight characters.';
      res.Valid = false;
    }

    if (newPassword != confirmPassword) {
      res.Valid = false;
      res.ConfirmPasswordErrorMessage = 'Both passwords must match.';
    }

    if (
      !this.utils.stringHasLowerCase(newPassword) ||
      !this.utils.stringHasUpperCase(newPassword) ||
      !this.utils.stringHasNumber(newPassword) ||
      !this.utils.stringHasSpecialCharacter(newPassword)
    ) {
      res.Valid = false;
      res.NewPasswordErrorMessage =
        'Password must have at least one upper case character, one lower case character, one number and one symbol.';
    }

    return res;
  }
  getUserName() {
    this.localStorageService.loadData(1);
    return this.localStorageService.observables.localRef.find((e) => {
      return e.ID == 'userSettings';
    })!.Obs;
  }

  getFeaturePermissions(): Observable<ProgramFeatures> {
    return this.httpClient.get<ProgramFeatures>(`${environment.apiUrl}userauth/featurepermissions`, this.httpOptions).pipe(
      map((res: any) => {
        return res.body!;
      })
    );
  }

  /*changePassword(oldPassword:string, newPassword: string){
    let req = {OldPassword: oldPassword, NewPassword: newPassword};
    return this.httpClient.post<ChangePassResponse>(`${environment.apiUrl}login/changepass`, req, this.httpOptions);
  }*/
}
